import React, { useEffect, useRef, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiAccessToken } from "../auth/acquireApiToken";
import { getApiScopes } from "../auth/apiScopes";
import { isMsalConfigured } from "../auth/msalInstance";
import { getCollections } from "../utils/collectionsData";
import { UPLOAD_STATUS, uploadFileDirect } from "../utils/directUpload";
import "./AdminPage.css";

function getLocalManualBearer() {
  const manual = process.env.REACT_APP_ADMIN_BEARER_TOKEN?.trim();
  if (!manual) return null;
  const isDev = process.env.NODE_ENV === "development";
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  return isDev && isLocalhost ? manual : null;
}

export default function AdminPage() {
  if (isMsalConfigured()) {
    return <AdminPageEntra />;
  }
  return <AdminPageManual />;
}

/**
 * Entra MSAL path: sign-in redirect, Bearer from delegated API token (or REACT_APP_ADMIN_BEARER_TOKEN override).
 */
function AdminPageEntra() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const scopesConfigured = getApiScopes().length > 0;

  useEffect(() => {
    const a = accounts[0];
    if (a) {
      instance.setActiveAccount(a);
    }
  }, [accounts, instance]);

  const getAuthHeaders = async () => {
    const manual = getLocalManualBearer();
    if (manual) {
      return { Authorization: `Bearer ${manual}` };
    }
    if (!scopesConfigured) {
      throw new Error(
        "Set REACT_APP_ENTRA_API_SCOPE or REACT_APP_ENTRA_AUDIENCE (must match backend ENTRA_AUDIENCE)."
      );
    }
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) {
      throw new Error("Sign in with Microsoft first.");
    }
    const token = await acquireApiAccessToken(instance, account);
    if (token === null) {
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const login = () => {
    instance.loginRedirect({ scopes: getApiScopes() });
  };

  const logout = () => {
    const account = instance.getActiveAccount() || accounts[0];
    instance.logoutRedirect({
      account,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  if (!scopesConfigured) {
    return (
      <div className="admin-panel">
        <h1 className="admin-title">Admin</h1>
        <p>
          Missing API scope configuration. Set{" "}
          <code>REACT_APP_ENTRA_API_SCOPE</code> or{" "}
          <code>REACT_APP_ENTRA_AUDIENCE</code> (same app ID URI family as the
          Function App <code>ENTRA_AUDIENCE</code>).
        </p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h1 className="admin-title">Admin Panel</h1>

      <div className="admin-section" style={{ marginBottom: 16 }}>
        {!isAuthenticated ? (
          <button type="button" onClick={login}>
            Sign in with Microsoft
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span>Signed in as {accounts[0]?.username ?? accounts[0]?.name}</span>
            <button type="button" onClick={logout}>
              Sign out
            </button>
          </div>
        )}
      </div>

      <UploadPanel getAuthHeaders={getAuthHeaders} />
    </div>
  );
}

/**
 * Fallback when Entra SPA env is not set: optional static bearer only (local/debug).
 */
function AdminPageManual() {
  const getAuthHeaders = async () => {
    const manual = getLocalManualBearer();
    if (!manual) {
      throw new Error(
        "Set REACT_APP_ENTRA_CLIENT_ID and REACT_APP_ENTRA_TENANT_ID for Microsoft sign-in, or set REACT_APP_ADMIN_BEARER_TOKEN for localhost development only."
      );
    }
    return { Authorization: `Bearer ${manual}` };
  };

  return (
    <div className="admin-panel">
      <h1 className="admin-title">Admin Panel</h1>
      <div className="admin-section" style={{ marginBottom: 16 }}>
        <p style={{ marginTop: 0 }}>
          Microsoft Entra sign-in is not configured for this build. Use{" "}
          <code>REACT_APP_ADMIN_BEARER_TOKEN</code> in{" "}
          <code>.env.development.local</code> for a manual bearer, or add{" "}
          <code>REACT_APP_ENTRA_*</code> variables and rebuild (see README).
        </p>
      </div>
      <UploadPanel getAuthHeaders={getAuthHeaders} />
    </div>
  );
}

function buildUploadMetadata(item) {
  const meta = {};
  if (item.title.trim()) meta.title = item.title.trim();
  if (item.description.trim()) meta.description = item.description.trim();
  if (item.collection.trim()) meta.collection = item.collection.trim();
  if (item.portfolio === "yes") meta.portfolio = true;
  if (item.portfolio === "no") meta.portfolio = false;
  return meta;
}

function describeUploadState(item) {
  switch (item.status) {
    case UPLOAD_STATUS.UPLOADING:
      return `Uploading ${item.progress || 0}%`;
    case UPLOAD_STATUS.PROCESSING:
      return "Processing…";
    case UPLOAD_STATUS.READY:
      return "Ready";
    case UPLOAD_STATUS.FAILED:
      return `Failed: ${item.message || "unknown error"}`;
    default:
      return "Queued";
  }
}

function UploadPanel({ getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [collectionsError, setCollectionsError] = useState(null);
  const nextKey = useRef(0);

  useEffect(() => {
    getCollections()
      .then((rows) => setCollectionOptions(rows.map((c) => ({ key: c.key, label: c.name || c.key }))))
      .catch((err) => setCollectionsError(err.message));
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map((f) => {
      nextKey.current += 1;
      return {
        key: `upload-${nextKey.current}`,
        file: f,
        title: "",
        description: "",
        collection: "",
        portfolio: "auto",
        status: UPLOAD_STATUS.QUEUED,
        progress: 0,
        message: "",
      };
    });
    setItems(newItems);
    setUploadStatus("");
  };

  const updateItem = (key, patch) => {
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  };

  const handleMetaChange = (key, field, value) => updateItem(key, { [field]: value });

  const removeItem = (key) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
  };

  /**
   * Files go to storage one at a time (bandwidth), then each is polled in the background while
   * the next one uploads. Already-finished READY files are not sent again.
   */
  const handleUpload = async () => {
    const pending = items.filter((item) => item.status !== UPLOAD_STATUS.READY);
    if (!pending.length || busy) return;
    setBusy(true);
    setUploadStatus(`Uploading ${pending.length} file(s)...`);

    const startUpload = (item) => {
      let markTransferred;
      const transferred = new Promise((resolve) => {
        markTransferred = resolve;
      });
      const done = uploadFileDirect({
        file: item.file,
        metadata: buildUploadMetadata(item),
        getAuthHeaders,
        onUpdate: (patch) => {
          updateItem(item.key, patch);
          if (patch.status !== UPLOAD_STATUS.UPLOADING) markTransferred(true);
        },
      }).then((final) => {
        if (final === null) {
          // Sign-in redirect started; the file was not sent.
          updateItem(item.key, { status: UPLOAD_STATUS.QUEUED, progress: 0 });
        }
        markTransferred(final !== null);
        return final;
      });
      return { transferred, done };
    };

    const processing = [];
    let redirected = false;
    for (const item of pending) {
      const { transferred, done } = startUpload(item);
      processing.push(done);
      // Start the next transfer once this one has left the browser (or failed).
      redirected = !(await transferred);
      if (redirected) break;
    }

    const results = await Promise.all(processing);
    setBusy(false);
    if (redirected) {
      setUploadStatus("");
      return;
    }
    const ready = results.filter((r) => r && r.status === UPLOAD_STATUS.READY).length;
    const failed = results.filter((r) => r && r.status === UPLOAD_STATUS.FAILED).length;
    setUploadStatus(
      failed
        ? `Finished: ${ready} ready, ${failed} failed.`
        : `Upload successful (${ready} file(s)).`
    );
  };

  return (
    <div className="admin-section">
      <h2>Upload Media</h2>
      <p>Choose files, add title and description, then upload.</p>
      <p className="admin-upload-hint">
        <strong>Auto</strong> fills from file metadata on the API. Use{" "}
        <strong>No</strong> under Portfolio if the image should not appear on
        the public portfolio page.
      </p>

      <input
        className="admin-upload-input"
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.mp4,.mov,.avi"
        aria-label="Choose media files"
        disabled={busy}
        onChange={handleFileChange}
      />

      {items.length > 0 && (
        <div className="admin-file-list">
          {items.map((item) => {
            // Metadata is fixed once a file is in flight or done.
            const locked = busy || item.status === UPLOAD_STATUS.READY;
            return (
            <div key={item.key} className="admin-file-metadata">
              <div className="admin-file-row">
                <strong>{item.file.name}</strong>
                <button
                  type="button"
                  className="admin-remove"
                  onClick={() => removeItem(item.key)}
                  disabled={busy}
                  aria-label="Remove file"
                >
                  Remove
                </button>
              </div>
              <div
                role="status"
                aria-live="polite"
                data-testid={`upload-state-${item.file.name}`}
                style={{ margin: "4px 0 8px" }}
              >
                {describeUploadState(item)}
                {item.status === UPLOAD_STATUS.UPLOADING && (
                  <progress
                    max={100}
                    value={item.progress || 0}
                    style={{ marginLeft: 8, verticalAlign: "middle" }}
                  />
                )}
              </div>
              <label>
                Title
                <input
                  type="text"
                  value={item.title}
                  disabled={locked}
                  onChange={(e) => handleMetaChange(item.key, "title", e.target.value)}
                  placeholder="Enter title"
                />
              </label>
              <label>
                Description
                <textarea
                  value={item.description}
                  disabled={locked}
                  onChange={(e) =>
                    handleMetaChange(item.key, "description", e.target.value)
                  }
                  placeholder="Enter description"
                  rows={3}
                />
              </label>
              <label>
                Collection
                <select
                  value={item.collection}
                  disabled={locked}
                  onChange={(e) =>
                    handleMetaChange(item.key, "collection", e.target.value)
                  }
                >
                  <option value="">Auto</option>
                  {collectionOptions.map((cat) => (
                    <option key={cat.key} value={cat.key}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {collectionsError && (
                  <span className="admin-upload-hint">
                    Failed to load collection list: {collectionsError}
                  </span>
                )}
              </label>
              <label>
                Add to Portfolio
                <select
                  value={item.portfolio}
                  disabled={locked}
                  onChange={(e) =>
                    handleMetaChange(item.key, "portfolio", e.target.value)
                  }
                >
                  <option value="auto">Auto</option>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
            </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={handleUpload}
        disabled={busy || !items.some((item) => item.status !== UPLOAD_STATUS.READY)}
        style={{ marginTop: 12 }}
      >
        {busy ? "Uploading…" : "Upload"}
      </button>
      {uploadStatus && (
        <div role="status" style={{ marginTop: 10 }}>
          {uploadStatus}
        </div>
      )}
    </div>
  );
}
